/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListAttributeKeysRequest,
  ListAttributeKeysSecurity,
} from "../models/operations/listattributekeys.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildListAttributeKeysQuery,
  ListAttributeKeysQueryData,
  prefetchListAttributeKeys,
  queryKeyListAttributeKeys,
} from "./listAttributeKeys.core.js";
export {
  buildListAttributeKeysQuery,
  type ListAttributeKeysQueryData,
  prefetchListAttributeKeys,
  queryKeyListAttributeKeys,
};

export type ListAttributeKeysQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listAttributeKeys telemetry
 *
 * @remarks
 * List distinct attribute keys available for filtering
 */
export function useListAttributeKeys(
  request: ListAttributeKeysRequest,
  security?: ListAttributeKeysSecurity | undefined,
  options?: QueryHookOptions<
    ListAttributeKeysQueryData,
    ListAttributeKeysQueryError
  >,
): UseQueryResult<ListAttributeKeysQueryData, ListAttributeKeysQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildListAttributeKeysQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listAttributeKeys telemetry
 *
 * @remarks
 * List distinct attribute keys available for filtering
 */
export function useListAttributeKeysSuspense(
  request: ListAttributeKeysRequest,
  security?: ListAttributeKeysSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ListAttributeKeysQueryData,
    ListAttributeKeysQueryError
  >,
): UseSuspenseQueryResult<
  ListAttributeKeysQueryData,
  ListAttributeKeysQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildListAttributeKeysQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setListAttributeKeysData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: ListAttributeKeysQueryData,
): ListAttributeKeysQueryData | undefined {
  const key = queryKeyListAttributeKeys(...queryKeyBase);

  return client.setQueryData<ListAttributeKeysQueryData>(key, data);
}

export function invalidateListAttributeKeys(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "telemetry",
      "listAttributeKeys",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllListAttributeKeys(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "telemetry", "listAttributeKeys"],
  });
}
